/**
 * BillingWarningBanner - Displays contextual billing warnings
 * 
 * Shows warnings for:
 * - Expiring trials
 * - Low credits
 * - Grace period
 * - Expired subscriptions
 */
import React from 'react';
import { Alert, Button, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useBilling } from '../../contexts/BillingContext';

interface BillingWarningBannerProps {
  variant?: 'standard' | 'compact';
}

const BillingWarningBanner: React.FC<BillingWarningBannerProps> = ({ variant = 'standard' }) => {
  const navigate = useNavigate();
  const { showBillingWarning, warningMessage, isAlwaysFree, billingStatus } = useBilling();

  // Don't show for always free accounts or if no warning
  if (isAlwaysFree || !showBillingWarning || !warningMessage) {
    return null;
  }

  // Determine severity based on status
  const getSeverity = () => {
    if (billingStatus?.subscription_status === 'expired') return 'error';
    if (billingStatus?.subscription_status === 'grace') return 'warning';
    return 'info';
  };

  const handleAction = () => {
    // Navigate to admin billing settings
    navigate('/admin?tab=settings');
  };

  if (variant === 'compact') {
    return (
      <Alert 
        severity={getSeverity()} 
        sx={{ 
          py: 0.5, 
          '& .MuiAlert-message': { 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            width: '100%',
          } 
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <span>{warningMessage}</span>
          <Button size="small" color="inherit" onClick={handleAction} sx={{ ml: 2 }}>
            Manage Billing
          </Button>
        </Box>
      </Alert>
    );
  }

  return (
    <Alert 
      severity={getSeverity()}
      action={
        <Button color="inherit" size="small" onClick={handleAction}>
          Manage Billing
        </Button>
      }
      sx={{ mb: 2 }}
    >
      {warningMessage}
    </Alert>
  );
};

export default BillingWarningBanner;

